import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
export function variantQuantity(cart, id) {
  return (cart?.items || []).reduce((total, item) => total + (String(item.variant_id) === String(id) ? item.quantity : 0), 0);
}
export function quantityToAdd(min, step, inCart) {
  const target = inCart < min ? min : min + (Math.floor((inCart - min) / step) + 1) * step;
  return target - inCart;
}
const root = () => window.Shopify?.routes?.root || '/';
let latestCart = null;
let cartAt = 0;
let cartFlight = null;
const stockChecks = new Map();
const busyVariants = new Set();
let revision = 0;
let queue = Promise.resolve();
let pending = 0;
function syncCards() {
  document.querySelectorAll('[data-bb-quick-add]').forEach(form => {
    const quantity = variantQuantity(latestCart, form.dataset.variant);
    const add = quantityToAdd(Number(form.dataset.min) || 1, Number(form.dataset.step) || 1, quantity);
    const max = form.dataset.max === undefined ? Infinity : Number(form.dataset.max);
    const button = form.querySelector('button');
    const exhausted = latestCart && quantity + add > max;
    const busy = busyVariants.has(form.dataset.variant);
    button.disabled = Boolean(busy || exhausted);
    button.textContent = busy ? 'Adding…' : exhausted ? 'Limit in cart' : 'Add to cart';
    form.setAttribute('aria-busy', String(busy));
    const stepper = form.querySelector('[data-bb-quick-stepper]');
    if (stepper) {
      button.hidden = quantity > 0;
      stepper.hidden = quantity === 0;
      form.querySelector('[data-bb-quick-count]').textContent = String(quantity);
      form.querySelector('[data-bb-quick-minus]').disabled = busy;
      form.querySelector('[data-bb-quick-plus]').disabled = Boolean(busy || exhausted);
    }
    form.querySelector('[name="quantity"]').value = add;
  });
}
export function decrementLine(cart, id, min = 1, step = 1) {
  const matches = (cart.items || []).filter(item => String(item.variant_id) === String(id));
  const line = matches.find(item => !item.selling_plan_allocation && !Object.keys(item.properties || {}).length) || matches[0];
  if (!line?.key) return null;
  const minimum = Number(line.quantity_rule?.min) || min;
  const increment = Number(line.quantity_rule?.increment) || step;
  const next = line.quantity - increment;
  return { id: line.key, quantity: next < minimum ? 0 : next };
}
async function remove(form) {
  const status = form.querySelector('[role="status"]');
  let deferred;
  try {
    latestCart = await currentCart();
    const change = decrementLine(latestCart, form.dataset.variant, Number(form.dataset.min) || 1, Number(form.dataset.step) || 1);
    if (!change) { status.textContent = 'This card is no longer in your bag.'; return; }
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(element => element.dataset.sectionId))].slice(0, 5);
    deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({ action: change.quantity ? 'update' : 'remove', context: 'cart', lines: [change], promise: deferred.promise }));
    const response = await fetch(root() + 'cart/change.js', {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...change, sections, sections_url: location.pathname })
    });
    const data = await response.json();
    const didError = !response.ok || Boolean(data.status || data.errors);
    // A successful change response already contains the whole authoritative cart.
    latestCart = didError ? await readCart() : acceptCart(data);
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(latestCart), detail: { items: latestCart.items, itemCount: latestCart.item_count, sections: data.sections, source: 'bb-quick-add', didError } });
    deferred = null;
    if (didError) throw new Error(data.description || data.message || data.errors || 'Your bag could not update. Please check it before trying again.');
    status.textContent = 'Bag quantity updated.';
  } catch (error) {
    if (deferred) {
      try {
        latestCart = await readCart();
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(latestCart), detail: { items: latestCart.items, itemCount: latestCart.item_count, source: 'bb-quick-add', didError: true } });
      } catch (refreshError) { deferred.reject(refreshError); }
    }
    status.textContent = error.message || 'Could not update this card. Please check your bag.';
  } finally { finish(form); }
}
function acceptCart(cart) { latestCart = cart; cartAt = Date.now(); return cart; }
async function readCart() {
  if (!cartFlight) cartFlight = (async () => {
    const response = await fetch(root() + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Your bag could not refresh. Please open your bag before trying again.');
    return acceptCart(await response.json());
  })().finally(() => { cartFlight = null; });
  return cartFlight;
}
function currentCart() { return latestCart && Date.now() - cartAt < 5000 ? Promise.resolve(latestCart) : readCart(); }
async function freshLimit(form) {
  const previous = stockChecks.get(form.dataset.variant);
  if (previous && Date.now() - previous < 15000) return;
  // Re-render this product's stock data, without relying on a stale homepage card.
  const url = new URL(form.dataset.productUrl, location.origin);
  if (url.origin !== location.origin) throw new Error('Product data must stay on this store.');
  url.search = ''; url.searchParams.set('section_id', 'bb-stock-feed');
  const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store' });
  if (!response.ok) throw new Error('Stock could not be checked. Open the product to try again.');
  const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
  const source = parsed.querySelector('[data-bb-stock-feed]');
  if (source?.dataset.simple !== 'true') throw new Error('Open this product to choose an option.');
  const row = [...parsed.querySelectorAll('[data-bb-stock]')].find(row => row.dataset.variant === form.dataset.variant);
  if (!row) throw new Error('This card has changed. Open its product page to choose an option.');
  if (row.dataset.available !== 'true') throw new Error('This card is no longer available.');
  document.querySelectorAll('[data-bb-quick-add]').forEach(copy => {
    if (copy.dataset.variant !== form.dataset.variant) return;
    ['min', 'step', 'max'].forEach(key => {
      if (row.dataset[key] === undefined) delete copy.dataset[key];
      else copy.dataset[key] = row.dataset[key];
    });
  });
  if (stockChecks.size >= 96) stockChecks.delete(stockChecks.keys().next().value);
  stockChecks.set(form.dataset.variant, Date.now());
}
async function add(form) {
  const status = form.querySelector('[role="status"]');
  let deferred;
  let responseData;
  try {
    // Independent reads overlap; recent successful checks can be reused briefly.
    const [, cart] = await Promise.all([freshLimit(form), currentCart()]);
    latestCart = cart;
    const inCart = variantQuantity(latestCart, form.dataset.variant);
    const amount = quantityToAdd(Number(form.dataset.min) || 1, Number(form.dataset.step) || 1, inCart);
    const max = form.dataset.max === undefined ? Infinity : Number(form.dataset.max);
    if (inCart + amount > max) throw new Error('The available quantity is already in your bag.');
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(element => element.dataset.sectionId))].slice(0, 5);
    deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product', lines: [{ merchandiseId: form.dataset.variant, quantity: amount }], promise: deferred.promise
    }));
    const response = await fetch(root() + 'cart/add.js', {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: form.dataset.variant, quantity: amount }], sections, sections_url: location.pathname })
    });
    responseData = await response.json();
    latestCart = await readCart(); // Also required for a partial add / 422 response.
    const didError = !response.ok || Boolean(responseData.status);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(latestCart),
      detail: { items: latestCart.items, itemCount: latestCart.item_count, sections: responseData.sections, source: 'bb-quick-add', didError }
    });
    deferred = null;
    if (didError) {
      stockChecks.delete(form.dataset.variant);
      throw new Error(responseData.description || responseData.message || 'The available quantity has changed. Please check your bag.');
    }
    status.textContent = 'Added to your bag.';
  } catch (error) {
    if (deferred) {
      try {
        latestCart = await readCart();
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(latestCart), detail: { items: latestCart.items, itemCount: latestCart.item_count, source: 'bb-quick-add', didError: true } });
      } catch (refreshError) { deferred.reject(refreshError); }
    }
    status.textContent = error.message || 'Could not add this card. Please check your bag.';
  } finally {
    finish(form);
  }
}

function finish(form) {
  delete form.dataset.busy; busyVariants.delete(form.dataset.variant); pending--; syncCards();
}
function begin(form) {
  if (busyVariants.has(form.dataset.variant)) return false;
  busyVariants.add(form.dataset.variant); form.dataset.busy = 'true'; pending++;
  form.querySelector('[role="status"]').textContent = '';
  syncCards(); return true;
}
document.addEventListener('submit', event => {
  const form = event.target.closest('[data-bb-quick-add]');
  if (!form) return;
  event.preventDefault();
  if (!begin(form)) return;
  queue = queue.then(() => add(form), () => add(form));
});
document.addEventListener('click', event => {
  const button = event.target.closest('[data-bb-quick-minus]');
  const form = button?.closest('[data-bb-quick-add]');
  if (!form) return;
  event.preventDefault();
  if (button.disabled || !begin(form)) return;
  queue = queue.then(() => remove(form), () => remove(form));
});
document.addEventListener(StandardEvents.cartLinesUpdate, event => {
  cartAt = 0;
  const current = ++revision;
  event.promise?.then(({ detail }) => {
    if (current !== revision) return;
    if (detail?.items) { acceptCart({ items: detail.items, item_count: detail.itemCount }); syncCards(); }
    else readCart().then(cart => { if (current === revision) { latestCart = cart; syncCards(); } }).catch(() => {});
  }).catch(() => {});
});
document.addEventListener('bb:cards-loaded', syncCards);
window.addEventListener('pageshow', () => { readCart().then(cart => { latestCart = cart; syncCards(); }).catch(() => {}); });
if (document.readyState !== 'loading') readCart().then(cart => { latestCart = cart; syncCards(); }).catch(() => {});
